/**
 * Persistent per-agent memory-sync pointer. Tracks each agent's
 * last-known chain-active memory id and when we last confirmed it.
 *
 * One JSON blob keyed by agent pubkey hex. Writes are atomic
 * (tmp + rename). Load is best-effort — parse failure resets to empty
 * rather than throwing.
 */
import { promises as fs } from "node:fs";
import path from "node:path";
import type { MemoryPointer } from "./sync.js";

interface PointerFile {
  version: 1;
  agents: Record<string, MemoryPointer>;
}

const EMPTY: PointerFile = { version: 1, agents: {} };

export class PointerStore {
  private cache: PointerFile | null = null;
  private loading: Promise<void> | null = null;
  constructor(private readonly filePath: string) {}

  /** Resolves the pointer for `agentPubkeyHex`, or a zero-pointer that will force a sync on first use. */
  async get(agentPubkeyHex: string): Promise<MemoryPointer> {
    await this.ensureLoaded();
    return this.cache!.agents[agentPubkeyHex] ?? { activeId: null, checkedAt: 0 };
  }

  /** Persists an updated pointer. Failures are swallowed to a logger callback (if provided) so sync never blocks the caller. */
  async set(
    agentPubkeyHex: string,
    pointer: MemoryPointer,
    onError?: (err: Error) => void,
  ): Promise<void> {
    await this.ensureLoaded();
    this.cache!.agents[agentPubkeyHex] = pointer;
    try {
      await this.persist(this.cache!);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  private async ensureLoaded(): Promise<void> {
    if (this.cache) return;
    if (!this.loading) this.loading = this.loadOnce();
    await this.loading;
  }

  private async loadOnce(): Promise<void> {
    try {
      const raw = await fs.readFile(this.filePath, "utf8");
      const parsed = JSON.parse(raw) as PointerFile;
      if (parsed && parsed.version === 1 && parsed.agents && typeof parsed.agents === "object") {
        this.cache = parsed;
        return;
      }
    } catch {
      // missing or corrupt → start fresh
    }
    this.cache = { ...EMPTY, agents: {} };
  }

  private async persist(file: PointerFile): Promise<void> {
    await fs.mkdir(path.dirname(this.filePath), { recursive: true });
    const tmp = `${this.filePath}.${process.pid}.tmp`;
    await fs.writeFile(tmp, JSON.stringify(file, null, 2), "utf8");
    await fs.rename(tmp, this.filePath);
  }
}

/** Default pointer-file location — `<workspaceDir>/.atbash/memory-pointer.json`. */
export function defaultPointerPath(workspaceDir: string = process.cwd()): string {
  return path.join(workspaceDir, ".atbash", "memory-pointer.json");
}
